import React, { useState, Children } from 'react';
import { PlusIcon, SearchIcon, EyeIcon, PrinterIcon, DownloadIcon, FileSpreadsheetIcon, FileTextIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { ClinicalHistory as ClinicalHistoryType } from '../../types';
import { formatDate } from '../../utils/helpers';
import { exportClinicalHistoriesToExcel } from '../../utils/excelExport';
export function ClinicalHistory() {
  const {
    patients,
    getPatient,
    clinicalHistories,
    addClinicalHistory
  } = useData();
  const {
    user
  } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState<ClinicalHistoryType | null>(null);
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  // Form state - Antecedentes Personales
  const [personalData, setPersonalData] = useState({
    fullName: '',
    birthDate: '',
    address: '',
    phone: '',
    email: '',
    age: 0,
    gender: '',
    occupation: '',
    numberOfChildren: 0,
    deliveryType: '',
    alcohol: '',
    tobacco: '',
    drugs: '',
    infusions: '',
    consultationReason: ''
  });
  // Form state - Antecedentes Clínicos
  const [clinicalData, setClinicalData] = useState({
    diseases: '',
    currentMedications: '',
    chronicDiseases: '',
    surgicalHistory: '',
    implantsOrDevices: '',
    implantType: '',
    allergies: ''
  });
  // Form state - Diagnóstico, Tratamiento, etc.
  const [diagnosis, setDiagnosis] = useState('');
  const [treatment, setTreatment] = useState('');
  const [medicalIndications, setMedicalIndications] = useState('');
  const [sessions, setSessions] = useState('');
  const [sessionNumber, setSessionNumber] = useState('');
  const [comments, setComments] = useState('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const filteredHistories = clinicalHistories.filter(h => {
    const patient = getPatient(h.patientId);
    return patient?.name.toLowerCase().includes(searchTerm.toLowerCase());
  });
  const patientOptions = patients.map(p => ({
    value: p.id,
    label: p.name
  }));
  const openNewForm = () => {
    setSelectedHistory(null);
    setSelectedPatientId('');
    resetForm();
    setIsFormOpen(true);
  };
  const openViewHistory = (history: ClinicalHistoryType) => {
    setSelectedHistory(history);
    setIsViewOpen(true);
  };
  const resetForm = () => {
    setPersonalData({
      fullName: '',
      birthDate: '',
      address: '',
      phone: '',
      email: '',
      age: 0,
      gender: '',
      occupation: '',
      numberOfChildren: 0,
      deliveryType: '',
      alcohol: '',
      tobacco: '',
      drugs: '',
      infusions: '',
      consultationReason: ''
    });
    setClinicalData({
      diseases: '',
      currentMedications: '',
      chronicDiseases: '',
      surgicalHistory: '',
      implantsOrDevices: '',
      implantType: '',
      allergies: ''
    });
    setDiagnosis('');
    setTreatment('');
    setMedicalIndications('');
    setSessions('');
    setSessionNumber('');
    setComments('');
    setFormErrors({});
  };
  const handlePatientSelect = (patientId: string) => {
    setSelectedPatientId(patientId);
    const patient = getPatient(patientId);
    if (patient) {
      setPersonalData({
        fullName: patient.name,
        birthDate: '',
        address: patient.address,
        phone: patient.phone,
        email: patient.email,
        age: patient.age,
        gender: patient.gender,
        occupation: '',
        numberOfChildren: 0,
        deliveryType: '',
        alcohol: '',
        tobacco: '',
        drugs: '',
        infusions: '',
        consultationReason: ''
      });
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!selectedPatientId) errors.patient = 'Debe seleccionar un paciente';
    if (!personalData.fullName) errors.fullName = 'El nombre es requerido';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    addClinicalHistory({
      patientId: selectedPatientId,
      date: new Date(),
      doctorId: user?.id || '',
      doctorName: user?.name || '',
      personalBackground: personalData,
      clinicalBackground: clinicalData,
      diagnosis,
      treatment,
      medicalIndications,
      sessions,
      sessionNumber,
      comments,
      doctorSignature: user?.name || ''
    });
    setIsFormOpen(false);
    resetForm();
  };
  const handlePrint = () => {
    // Add a small delay to ensure styles are applied
    setTimeout(() => {
      window.print();
    }, 100);
  };
  const handleExportToExcel = async () => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportClinicalHistoriesToExcel(clinicalHistories, (patientId: string) => {
      const patient = getPatient(patientId);
      return patient?.name || '-';
    });
    setIsExporting(false);
  };
  return <div className="space-y-6" data-id="element-228">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-229">
        <div data-id="element-230">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-231">
            Historial Clínico (Medicina Estética)
          </h1>
          <p className="text-gray-500 dark:text-gray-400" data-id="element-232">
            Gestión de historiales clínicos de pacientes
          </p>
        </div>
        <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-234" />} onClick={openNewForm} data-id="element-233">
          Nuevo Historial
        </Button>
      </div>

      {/* Beautiful Export Card */}
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border-blue-200 dark:border-blue-800" data-id="element-235">
        <div className="flex items-center justify-between" data-id="element-236">
          <div className="flex items-center gap-4" data-id="element-237">
            <div className="w-14 h-14 rounded-xl bg-blue-500 dark:bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20" data-id="element-238">
              <FileSpreadsheetIcon className="w-7 h-7 text-white" data-id="element-239" />
            </div>
            <div data-id="element-240">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1" data-id="element-241">
                Exportar Base de Datos
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-242">
                Descarga todos los historiales clínicos en formato Excel
              </p>
              <div className="flex items-center gap-4 mt-2" data-id="element-243">
                <div className="flex items-center gap-1.5 text-sm text-blue-700 dark:text-blue-400" data-id="element-244">
                  <FileTextIcon className="w-4 h-4" data-id="element-245" />
                  <span className="font-medium" data-id="element-246">
                    {clinicalHistories.length} historiales
                  </span>
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-247">
                  • Incluye: Paciente, Doctor, Diagnóstico, Tratamiento
                </div>
              </div>
            </div>
          </div>
          <Button leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-249" />} onClick={handleExportToExcel} isLoading={isExporting} className="shadow-lg shadow-blue-500/20 hover:shadow-xl hover:shadow-blue-500/30 transition-all duration-200" data-id="element-248">
            {isExporting ? 'Exportando...' : 'Descargar Excel'}
          </Button>
        </div>
      </Card>

      {/* Search */}
      <Card data-id="element-250">
        <div className="relative" data-id="element-251">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-252" />
          <Input placeholder="Buscar por paciente..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-id="element-253" />
        </div>
      </Card>

      {/* Histories List */}
      <Card padding="none" data-id="element-254">
        <div className="overflow-x-auto" data-id="element-255">
          <table className="w-full" data-id="element-256">
            <thead data-id="element-257">
              <tr className="border-b border-gray-200 dark:border-gray-700" data-id="element-258">
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-259">
                  Fecha
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-260">
                  Paciente
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-261">
                  Doctor
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-262">
                  Motivo de Consulta
                </th>
                <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-263">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-264">
              {filteredHistories.length === 0 ? <tr data-id="element-265">
                  <td colSpan={5} className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-266">
                    No hay historiales clínicos registrados
                  </td>
                </tr> : filteredHistories.map(history => {
              const patient = getPatient(history.patientId);
              return <tr key={history.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50" data-id="element-267">
                      <td className="py-4 px-6 text-sm text-gray-900 dark:text-gray-100" data-id="element-268">
                        {formatDate(history.date)}
                      </td>
                      <td className="py-4 px-6" data-id="element-269">
                        <p className="font-medium text-gray-900 dark:text-white" data-id="element-270">
                          {patient?.name}
                        </p>
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-900 dark:text-gray-100" data-id="element-271">
                        Dr. {history.doctorName}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600 dark:text-gray-400" data-id="element-272">
                        {history.personalBackground.consultationReason || '-'}
                      </td>
                      <td className="py-4 px-6" data-id="element-273">
                        <div className="flex items-center justify-end gap-2" data-id="element-274">
                          <button onClick={() => openViewHistory(history)} className="p-2 text-gray-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-lg transition-colors" title="Ver historial" data-id="element-275">
                            <EyeIcon className="w-4 h-4" data-id="element-276" />
                          </button>
                        </div>
                      </td>
                    </tr>;
            })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title="Nuevo Historial Clínico (Medicina Estética)" size="full" data-id="element-277">
        <form onSubmit={handleSubmit} className="space-y-8" data-id="element-278">
          {/* Selección de Paciente */}
          <div className="bg-sky-50 dark:bg-sky-900/20 p-4 rounded-lg" data-id="element-279">
            <Select label="Seleccionar Paciente" options={patientOptions} value={selectedPatientId} onChange={handlePatientSelect} error={formErrors.patient} searchable data-id="element-280" />
          </div>

          {/* Antecedentes Personales */}
          <div data-id="element-281">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-700" data-id="element-282">
              Antecedentes Personales
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-283">
              <Input label="Nombre y Apellido" value={personalData.fullName} onChange={e => setPersonalData({
              ...personalData,
              fullName: e.target.value
            })} error={formErrors.fullName} data-id="element-284" />
              <Input type="date" label="Fecha de Nacimiento (F.N.)" value={personalData.birthDate} onChange={e => setPersonalData({
              ...personalData,
              birthDate: e.target.value
            })} data-id="element-285" />
              <Input label="Dirección" value={personalData.address} onChange={e => setPersonalData({
              ...personalData,
              address: e.target.value
            })} data-id="element-286" />
              <Input label="Tel./Cel." value={personalData.phone} onChange={e => setPersonalData({
              ...personalData,
              phone: e.target.value
            })} data-id="element-287" />
              <Input type="email" label="E-mail" value={personalData.email} onChange={e => setPersonalData({
              ...personalData,
              email: e.target.value
            })} data-id="element-288" />
              <Input type="number" label="Edad" value={personalData.age} onChange={e => setPersonalData({
              ...personalData,
              age: parseInt(e.target.value) || 0
            })} data-id="element-289" />
              <Input label="Sexo" value={personalData.gender} onChange={e => setPersonalData({
              ...personalData,
              gender: e.target.value
            })} data-id="element-290" />
              <Input label="Ocupación u Oficio" value={personalData.occupation} onChange={e => setPersonalData({
              ...personalData,
              occupation: e.target.value
            })} data-id="element-291" />
              <Input type="number" label="N° de Hijos" value={personalData.numberOfChildren} onChange={e => setPersonalData({
              ...personalData,
              numberOfChildren: parseInt(e.target.value) || 0
            })} data-id="element-292" />
              <Input label="Tipo de Parto" value={personalData.deliveryType} onChange={e => setPersonalData({
              ...personalData,
              deliveryType: e.target.value
            })} placeholder="Ej: C/A, Normal" data-id="element-293" />
              <Input label="Alcohol" value={personalData.alcohol} onChange={e => setPersonalData({
              ...personalData,
              alcohol: e.target.value
            })} data-id="element-294" />
              <Input label="Tabaco" value={personalData.tobacco} onChange={e => setPersonalData({
              ...personalData,
              tobacco: e.target.value
            })} data-id="element-295" />
              <Input label="Drogas" value={personalData.drugs} onChange={e => setPersonalData({
              ...personalData,
              drugs: e.target.value
            })} data-id="element-296" />
              <Input label="Infusiones" value={personalData.infusions} onChange={e => setPersonalData({
              ...personalData,
              infusions: e.target.value
            })} data-id="element-297" />
              <div className="md:col-span-3" data-id="element-298">
                <Textarea label="Motivo de la Consulta" value={personalData.consultationReason} onChange={e => setPersonalData({
                ...personalData,
                consultationReason: e.target.value
              })} rows={2} data-id="element-299" />
              </div>
            </div>
          </div>

          {/* Antecedentes Clínicos */}
          <div data-id="element-300">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-700" data-id="element-301">
              Antecedentes Clínicos
            </h3>
            <div className="grid grid-cols-1 gap-4" data-id="element-302">
              <Textarea label="Enfermedades" value={clinicalData.diseases} onChange={e => setClinicalData({
              ...clinicalData,
              diseases: e.target.value
            })} rows={2} data-id="element-303" />
              <Textarea label="Medicamentos que Toma Actualmente" value={clinicalData.currentMedications} onChange={e => setClinicalData({
              ...clinicalData,
              currentMedications: e.target.value
            })} rows={2} data-id="element-304" />
              <Textarea label="Antecedentes Enfermedades Crónicas" value={clinicalData.chronicDiseases} onChange={e => setClinicalData({
              ...clinicalData,
              chronicDiseases: e.target.value
            })} rows={2} data-id="element-305" />
              <Textarea label="Antecedentes Quirúrgicos" value={clinicalData.surgicalHistory} onChange={e => setClinicalData({
              ...clinicalData,
              surgicalHistory: e.target.value
            })} rows={2} data-id="element-306" />
              <div className="grid grid-cols-2 gap-4" data-id="element-307">
                <Input label="Uso de Implantes o Dispositivos" value={clinicalData.implantsOrDevices} onChange={e => setClinicalData({
                ...clinicalData,
                implantsOrDevices: e.target.value
              })} data-id="element-308" />
                <Input label="Tipo" value={clinicalData.implantType} onChange={e => setClinicalData({
                ...clinicalData,
                implantType: e.target.value
              })} data-id="element-309" />
              </div>
              <Textarea label="Alergias" value={clinicalData.allergies} onChange={e => setClinicalData({
              ...clinicalData,
              allergies: e.target.value
            })} rows={2} data-id="element-310" />
            </div>
          </div>

          {/* Diagnóstico */}
          <div data-id="element-311">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-700" data-id="element-312">
              Diagnóstico
            </h3>
            <Textarea value={diagnosis} onChange={e => setDiagnosis(e.target.value)} rows={4} placeholder="Escriba el diagnóstico..." data-id="element-313" />
          </div>

          {/* Tratamiento */}
          <div data-id="element-314">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-700" data-id="element-315">
              Tratamiento
            </h3>
            <Textarea value={treatment} onChange={e => setTreatment(e.target.value)} rows={4} placeholder="Escriba el tratamiento..." data-id="element-316" />
          </div>

          {/* Indicaciones Médicas */}
          <div data-id="element-317">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-700" data-id="element-318">
              Indicaciones Médicas
            </h3>
            <Textarea value={medicalIndications} onChange={e => setMedicalIndications(e.target.value)} rows={4} placeholder="Escriba las indicaciones médicas para el paciente..." data-id="element-319" />
          </div>

          {/* Sesiones */}
          <div data-id="element-320">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-700" data-id="element-321">
              Sesiones
            </h3>
            <div className="grid grid-cols-2 gap-4" data-id="element-322">
              <Input label="Sesiones" value={sessions} onChange={e => setSessions(e.target.value)} placeholder="Descripción de sesiones" data-id="element-323" />
              <Input label="N° de Sesión" value={sessionNumber} onChange={e => setSessionNumber(e.target.value)} placeholder="Número de sesión" data-id="element-324" />
            </div>
          </div>

          {/* Comentarios */}
          <div data-id="element-325">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-700" data-id="element-326">
              Comentarios
            </h3>
            <Textarea value={comments} onChange={e => setComments(e.target.value)} rows={3} placeholder="Comentarios adicionales..." data-id="element-327" />
          </div>

          {/* Firma del Doctor */}
          <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg" data-id="element-328">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2" data-id="element-329">
              Firma del Dr. (a)
            </p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-330">
              {user?.name}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-331">
              Fecha: {formatDate(new Date())}
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-332">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-333">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-334">Guardar Historial Clínico</Button>
          </div>
        </form>
      </Modal>

      {/* View Modal with Print-Optimized Layout */}
      <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title="Historial Clínico (Medicina Estética)" size="full" data-id="element-335">
        {selectedHistory && <div className="print-clinical-history" data-id="element-336">
            {/* Print Header (hidden on screen, visible on print) */}
            <div className="print-header hidden print:block" data-id="element-337">
              <img src="https://cdn.magicpatterns.com/uploads/a741bfNsYBMGGZNxniTt4s/images.jpg" alt="Logo" data-id="element-338" />
              <h1 data-id="element-339">HISTORIA CLÍNICA</h1>
              <p data-id="element-340">Medicina Estética</p>
              <div className="print-date" data-id="element-341">
                Fecha: {formatDate(selectedHistory.date)}
              </div>
            </div>

            {/* Screen Header (visible on screen, hidden on print) */}
            <div className="print:hidden flex items-center justify-between pb-4 border-b-2 border-sky-500 mb-6" data-id="element-342">
              <div className="flex items-center gap-4" data-id="element-343">
                <img src="https://cdn.magicpatterns.com/uploads/a741bfNsYBMGGZNxniTt4s/images.jpg" alt="Savidna Logo" className="w-16 h-16 rounded-full" data-id="element-344" />
                <div data-id="element-345">
                  <h2 className="text-2xl font-bold text-sky-600 dark:text-sky-400" data-id="element-346">
                    HISTORIA CLÍNICA
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400" data-id="element-347">
                    (Medicina Estética)
                  </p>
                </div>
              </div>
              <div className="text-right" data-id="element-348">
                <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-349">
                  Fecha:
                </p>
                <p className="font-semibold text-gray-900 dark:text-white" data-id="element-350">
                  {formatDate(selectedHistory.date)}
                </p>
              </div>
            </div>

            {/* Patient Info Box */}
            <div className="print-patient-info mb-6" data-id="element-351">
              <div data-id="element-352">
                <strong data-id="element-353">Paciente:</strong>{' '}
                {selectedHistory.personalBackground.fullName}
              </div>
              <div data-id="element-354">
                <strong data-id="element-355">Edad:</strong> {selectedHistory.personalBackground.age}{' '}
                años
              </div>
              <div data-id="element-356">
                <strong data-id="element-357">Sexo:</strong>{' '}
                {selectedHistory.personalBackground.gender}
              </div>
              <div data-id="element-358">
                <strong data-id="element-359">Teléfono:</strong>{' '}
                {selectedHistory.personalBackground.phone}
              </div>
              <div data-id="element-360">
                <strong data-id="element-361">Email:</strong>{' '}
                {selectedHistory.personalBackground.email}
              </div>
              <div data-id="element-362">
                <strong data-id="element-363">Fecha:</strong> {formatDate(selectedHistory.date)}
              </div>
            </div>

            {/* Content sections */}
            <div className="space-y-6" data-id="element-364">
              {/* Antecedentes Personales */}
              <div className="print-section" data-id="element-365">
                <h2 data-id="element-366">Antecedentes Personales</h2>
                <div className="print-field-group" data-id="element-367">
                  <div className="print-field" data-id="element-368">
                    <div className="print-field-label" data-id="element-369">Ocupación:</div>
                    <div className="print-field-value" data-id="element-370">
                      {selectedHistory.personalBackground.occupation || '-'}
                    </div>
                  </div>
                  <div className="print-field" data-id="element-371">
                    <div className="print-field-label" data-id="element-372">N° de hijos:</div>
                    <div className="print-field-value" data-id="element-373">
                      {selectedHistory.personalBackground.numberOfChildren}
                    </div>
                  </div>
                  <div className="print-field" data-id="element-374">
                    <div className="print-field-label" data-id="element-375">Tipo de parto:</div>
                    <div className="print-field-value" data-id="element-376">
                      {selectedHistory.personalBackground.deliveryType || '-'}
                    </div>
                  </div>
                  <div className="print-field" data-id="element-377">
                    <div className="print-field-label" data-id="element-378">Alcohol:</div>
                    <div className="print-field-value" data-id="element-379">
                      {selectedHistory.personalBackground.alcohol || '-'}
                    </div>
                  </div>
                  <div className="print-field" data-id="element-380">
                    <div className="print-field-label" data-id="element-381">Tabaco:</div>
                    <div className="print-field-value" data-id="element-382">
                      {selectedHistory.personalBackground.tobacco || '-'}
                    </div>
                  </div>
                  <div className="print-field" data-id="element-383">
                    <div className="print-field-label" data-id="element-384">Drogas:</div>
                    <div className="print-field-value" data-id="element-385">
                      {selectedHistory.personalBackground.drugs || '-'}
                    </div>
                  </div>
                  <div className="print-field print-field-full" data-id="element-386">
                    <div className="print-field-label" data-id="element-387">Infusiones:</div>
                    <div className="print-field-value" data-id="element-388">
                      {selectedHistory.personalBackground.infusions || '-'}
                    </div>
                  </div>
                  <div className="print-field print-field-full" data-id="element-389">
                    <div className="print-field-label" data-id="element-390">
                      Motivo de la consulta:
                    </div>
                    <div className="print-field-value" data-id="element-391">
                      {selectedHistory.personalBackground.consultationReason || '-'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Antecedentes Clínicos */}
              <div className="print-section" data-id="element-392">
                <h2 data-id="element-393">Antecedentes Clínicos</h2>
                <div className="space-y-3" data-id="element-394">
                  <div className="print-field" data-id="element-395">
                    <div className="print-field-label" data-id="element-396">Enfermedades:</div>
                    <div className="print-field-value" data-id="element-397">
                      {selectedHistory.clinicalBackground.diseases || '-'}
                    </div>
                  </div>
                  <div className="print-field" data-id="element-398">
                    <div className="print-field-label" data-id="element-399">
                      Medicamentos actuales:
                    </div>
                    <div className="print-field-value" data-id="element-400">
                      {selectedHistory.clinicalBackground.currentMedications || '-'}
                    </div>
                  </div>
                  <div className="print-field" data-id="element-401">
                    <div className="print-field-label" data-id="element-402">
                      Enfermedades crónicas:
                    </div>
                    <div className="print-field-value" data-id="element-403">
                      {selectedHistory.clinicalBackground.chronicDiseases || '-'}
                    </div>
                  </div>
                  <div className="print-field" data-id="element-404">
                    <div className="print-field-label" data-id="element-405">
                      Antecedentes quirúrgicos:
                    </div>
                    <div className="print-field-value" data-id="element-406">
                      {selectedHistory.clinicalBackground.surgicalHistory || '-'}
                    </div>
                  </div>
                  <div className="print-field" data-id="element-407">
                    <div className="print-field-label" data-id="element-408">
                      Implantes/Dispositivos:
                    </div>
                    <div className="print-field-value" data-id="element-409">
                      {selectedHistory.clinicalBackground.implantsOrDevices || '-'}
                      {selectedHistory.clinicalBackground.implantType && ` - Tipo: ${selectedHistory.clinicalBackground.implantType}`}
                    </div>
                  </div>
                  <div className="print-field" data-id="element-410">
                    <div className="print-field-label" data-id="element-411">Alergias:</div>
                    <div className="print-field-value" data-id="element-412">
                      {selectedHistory.clinicalBackground.allergies || '-'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Diagnóstico */}
              <div className="print-section" data-id="element-413">
                <h2 data-id="element-414">Diagnóstico</h2>
                <div className="print-field-value whitespace-pre-wrap" data-id="element-415">
                  {selectedHistory.diagnosis || '-'}
                </div>
              </div>

              {/* Tratamiento */}
              <div className="print-section" data-id="element-416">
                <h2 data-id="element-417">Tratamiento</h2>
                <div className="print-field-value whitespace-pre-wrap" data-id="element-418">
                  {selectedHistory.treatment || '-'}
                </div>
              </div>

              {/* Indicaciones Médicas */}
              <div className="print-section" data-id="element-419">
                <h2 data-id="element-420">Indicaciones Médicas</h2>
                <div className="print-field-value whitespace-pre-wrap" data-id="element-421">
                  {selectedHistory.medicalIndications || '-'}
                </div>
              </div>

              {/* Sesiones */}
              <div className="print-section" data-id="element-422">
                <h2 data-id="element-423">Sesiones</h2>
                <div className="print-field-group" data-id="element-424">
                  <div className="print-field" data-id="element-425">
                    <div className="print-field-label" data-id="element-426">Sesiones:</div>
                    <div className="print-field-value" data-id="element-427">
                      {selectedHistory.sessions || '-'}
                    </div>
                  </div>
                  <div className="print-field" data-id="element-428">
                    <div className="print-field-label" data-id="element-429">N° de sesión:</div>
                    <div className="print-field-value" data-id="element-430">
                      {selectedHistory.sessionNumber || '-'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Comentarios */}
              <div className="print-section" data-id="element-431">
                <h2 data-id="element-432">Comentarios</h2>
                <div className="print-field-value whitespace-pre-wrap" data-id="element-433">
                  {selectedHistory.comments || '-'}
                </div>
              </div>

              {/* Firma */}
              <div className="print-signature" data-id="element-434">
                <div className="print-signature-line" data-id="element-435">
                  <p className="font-bold text-gray-900 dark:text-white" data-id="element-436">
                    {selectedHistory.doctorSignature}
                  </p>
                </div>
                <p data-id="element-437">Firma del Dr. (a)</p>
              </div>
            </div>

            {/* Actions (hidden on print) */}
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700 no-print print:hidden" data-id="element-438">
              <Button variant="outline" leftIcon={<PrinterIcon className="w-4 h-4" data-id="element-440" />} onClick={handlePrint} data-id="element-439">
                Imprimir
              </Button>
              <Button onClick={() => setIsViewOpen(false)} data-id="element-441">Cerrar</Button>
            </div>
          </div>}
      </Modal>
    </div>;
}